import React, { useState, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { differenceInDays, startOfDay } from 'date-fns';
import { ShiftBreak, ShiftLabel } from '../types';
import FormattedDate from '../../../../../../components/date/FormattedDate';
import { formatTime24h } from '../../../../../../utils/dateUtils';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { TransferShiftModal } from './TransferShiftModal';
import {
  ShiftChangeRequestApprovalStatus,
  useShiftChangeRequestsByShiftIdQuery,
} from '../../../../__generated__/graphql';
import { ShiftChangeRequestsList } from '../ShiftChangeRequestsList';
import { useUserInfo } from '../../../../context/UserInfo';
import Loading from './Loading';

type ShiftSidePanelProps = {
  shiftId: string;
};

type ShiftSidePanelContentProps = {
  shiftId: string;
  showTransferButton?: boolean;
};

export const ShiftSidePanel: React.FC<ShiftSidePanelProps> = ({ shiftId }) => {
  return (
    <div className="p-4 d-flex flex-column" style={{ minHeight: '100%' }}>
      <ShiftSidePanelContent shiftId={shiftId} />
    </div>
  );
};

export const ShiftSidePanelContent: React.FC<ShiftSidePanelContentProps> = ({
  shiftId,
  showTransferButton = true,
}) => {
  const t = useNamespacedTranslation('schedulerShifts');
  const authEmployee = useUserInfo();
  const [showTransferModal, setShowTransferModal] = useState(false);

  const { data, loading } = useShiftChangeRequestsByShiftIdQuery({
    variables: {
      shiftId,
    },
    skip: !shiftId,
  });

  const isShiftEmployee = useMemo(() => {
    if (!data?.schedulerShift?.flair__Resource_Employee__c) {
      return false;
    }
    return data.schedulerShift.flair__Resource_Employee__c === authEmployee.id;
  }, [authEmployee, data]);

  const shiftChangeRequests = useMemo(() => {
    if (!data?.schedulerShift?.Id) {
      return [];
    }
    return data.schedulerShift.shiftChangeRequests;
  }, [data]);

  const fetchedBreaks = useMemo(() => {
    if (!data?.schedulerShift?.breaks) {
      return [];
    }
    return data.schedulerShift.breaks.map((breakItem) => ({
      id: breakItem.Id,
      startDate: new Date(breakItem.flair__Start_Datetime__c),
      endDate: new Date(breakItem.flair__End_Datetime__c),
    }));
  }, [data]);

  const fetchedLabels = useMemo(() => {
    if (!data?.schedulerShift?.shiftLabels) {
      return [];
    }
    return data.schedulerShift.shiftLabels.map((label) => ({
      id: label.Id,
      name: label.Name,
      tagName: label.flair__Tag_Name__c || '',
    }));
  }, [data]);

  const canTransferShift = useMemo(() => {
    if (!data?.schedulerShift) {
      return false;
    }

    return data.schedulerShift.flair__Allow_Shift_Transfer__c === true;
  }, [data]);

  const transferShiftDisabledInfo = useMemo(() => {
    if (!data?.schedulerShift) {
      return { disabled: false, message: null };
    }

    const shiftStartDate = startOfDay(
      new Date(data.schedulerShift.flair__Start_Datetime__c),
    );
    const today = startOfDay(new Date());
    const daysUntilShift = differenceInDays(shiftStartDate, today);

    if (daysUntilShift <= 0) {
      return {
        disabled: true,
        message: t('shiftSidePanel.cannotTransferPastShift'),
      };
    }

    const minDaysBeforeShiftTransfer =
      data.schedulerShift.flair__Min_Days_Before_Shift_Transfer__c;

    if (
      minDaysBeforeShiftTransfer !== null &&
      minDaysBeforeShiftTransfer !== undefined &&
      daysUntilShift <= minDaysBeforeShiftTransfer
    ) {
      return {
        disabled: true,
        message: t('shiftSidePanel.cannotTransferShiftTooSoon', {
          count: minDaysBeforeShiftTransfer,
        }),
      };
    }

    return { disabled: false, message: null };
  }, [data, t]);

  if (loading) {
    return <Loading />;
  }

  if (!data?.schedulerShift) {
    return <div>{t('shiftSidePanel.shiftNotFound')}</div>;
  }

  const displayStartDate = new Date(
    data.schedulerShift.flair__Start_Datetime__c,
  );
  const displayEndDate = new Date(data.schedulerShift.flair__End_Datetime__c);

  const hasPendingRequest = shiftChangeRequests.some(
    (request) =>
      request.flair__Approval_Status__c ===
      ShiftChangeRequestApprovalStatus.Pending,
  );

  return (
    <>
      <div className="flex-grow-1">
        <Section
          icon={<FlairIcon icon="calendar" size="sm" />}
          value={
            <>
              <div>
                <FormattedDate
                  day={displayStartDate}
                  format="day-with-weekday"
                />
              </div>
              <div>{`${formatTime24h(displayStartDate)} - ${formatTime24h(
                displayEndDate,
              )}`}</div>
            </>
          }
        />
        {fetchedBreaks.length > 0 && (
          <Section
            label={t('shiftSidePanel.breaks')}
            value={formatBreaks(fetchedBreaks)}
            icon={<FlairIcon icon="cafe-outline" size="sm" />}
          />
        )}
        {fetchedLabels && fetchedLabels.length > 0 && (
          <Section
            label={t('shiftSidePanel.labels')}
            value={formatLabels(fetchedLabels)}
            icon={<FlairIcon icon="mic-outline" size="sm" />}
          />
        )}
        {!!data?.schedulerShift?.flair__Employee_Notes__c && (
          <Section
            label={t('shiftSidePanel.employeeNotes')}
            value={data.schedulerShift.flair__Employee_Notes__c}
            icon={<FlairIcon icon="document-outline" size="sm" />}
          />
        )}
        {shiftChangeRequests.length > 0 && (
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex align-items-start mb-3 gap-3">
              <div className="text-muted flex-shrink-0 mt-1">
                <FlairIcon icon="clipboard-outline" size="sm" />
              </div>
              <div className="flex-grow-1 d-flex flex-column gap-1">
                <div className="fw-semibold mb-1">
                  {t('shiftSidePanel.shiftChangeRequests')}
                </div>
                <ShiftChangeRequestsList requests={shiftChangeRequests} />
              </div>
            </div>
          </div>
        )}
      </div>
      {showTransferButton && isShiftEmployee && canTransferShift && (
        <div className="mt-auto pt-3 border-top">
          <Button
            variant="primary"
            className="w-100"
            onClick={() => setShowTransferModal(true)}
            disabled={hasPendingRequest || transferShiftDisabledInfo.disabled}>
            {t('transferShiftButton')}
          </Button>
          {hasPendingRequest && (
            <div className="text-muted small mt-2 text-center">
              {t('shiftSidePanel.pendingRequestMessage')}
            </div>
          )}
          {!hasPendingRequest && transferShiftDisabledInfo.disabled && (
            <div className="text-muted small mt-2 text-center">
              {transferShiftDisabledInfo.message}
            </div>
          )}
        </div>
      )}
      {showTransferButton && (
        <TransferShiftModal
          show={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          shiftId={shiftId}
        />
      )}
    </>
  );
};

const formatBreaks = (breaks: ShiftBreak[]): string => {
  return breaks
    .map((breakItem) => {
      const start = formatTime24h(new Date(breakItem.startDate));
      const end = formatTime24h(new Date(breakItem.endDate));
      return `${start} - ${end}`;
    })
    .join(', ');
};

const formatLabels = (labels: ShiftLabel[]): string => {
  return labels.map((label) => label.name).join(', ');
};

type SectionProps = {
  label?: string;
  value: React.ReactNode;
  icon: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ label, value, icon }) => {
  if (!value) {
    return null;
  }

  return (
    <div className="d-flex align-items-start mb-3 gap-3">
      {!!icon && <div className="text-muted flex-shrink-0 mt-1">{icon}</div>}
      <div className="flex-grow-1 d-flex flex-column gap-1">
        {label && <div className="fw-semibold mb-1">{label}</div>}
        <div style={{ lineHeight: 1.4 }}>{value}</div>
      </div>
    </div>
  );
};
